import { Buffer } from "buffer";

import { useCodatAuth } from "@codat/client-cookie-manager";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { Options, UseAxios, UseAxiosResult, makeUseAxios } from "axios-hooks";
import { stringify } from "query-string";
import { ApiError } from "shared";
import Cookies from "universal-cookie";
import formatUrl from "utils/formatUrl";

import config from "config";
import routes from "config/urls/api";

const cookies = new Cookies();

const emptyGuid = "00000000-0000-0000-0000-000000000000";

export interface PagedResponse<T> {
  results: T[];
  pageNumber: number;
  pageSize: number;
  totalResults: number;
}

interface Api {
  instance: AxiosInstance;
  useAxios: UseAxios;
}

const userManagementInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.USER_MANAGEMENT_API_URL,
});

const portalInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.PORTAL_API_URL,
});

const api = {
  userManagementInstance: {
    instance: userManagementInstance,
    useAxios: makeUseAxios({
      axios: userManagementInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  portalInstance: {
    instance: portalInstance,
    useAxios: makeUseAxios({
      axios: portalInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
};

export const UseSelectUserManagementApi = <TResponse, TBody = unknown>(
  requestConfig: AxiosRequestConfig,
  options?: Options
): UseAxiosResult<TResponse, TBody, ApiError> => {
  return useUserManagementApi(requestConfig, "", emptyGuid, options);
};

export const UseManageUserManagementApi = <TResponse, TBody = unknown>(
  requestConfig: AxiosRequestConfig,
  options?: Options
): UseAxiosResult<TResponse, TBody, ApiError> => {
  const auth = useCodatAuth();
  const token = auth.userData?.access_token;
  const clientId = token ? tryGetClientIdFromTokenOrRedirect(token) : emptyGuid;

  return useUserManagementApi(
    requestConfig,
    routes.manage.base,
    clientId || emptyGuid,
    options
  );
};

export const UseManagePortalApi = <TResponse>(
  requestConfig: AxiosRequestConfig,
  options?: Options
): UseAxiosResult<TResponse, ApiError> => {
  return usePortalApi(requestConfig, options);
};

export const GetClientId = (): string => {
  const auth = useCodatAuth();
  const token = auth.userData?.access_token;
  return token ? tryGetClientIdFromTokenOrRedirect(token) : emptyGuid;
};

const useUserManagementApi = <TResponse, TBody>(
  requestConfig: AxiosRequestConfig,
  base: string,
  clientId: string,
  options?: Options
): UseAxiosResult<TResponse, TBody, ApiError> => {
  const auth = useCodatAuth();

  const token = auth.userData?.access_token;

  const result = api.userManagementInstance.useAxios<
    TResponse,
    TBody,
    ApiError
  >(
    {
      ...requestConfig,
      url: formatUrl(`${base}${requestConfig.url}`, { clientId }),
      headers: {
        ...(requestConfig.headers || {}),
        authorization: `Bearer ${token}`,
      },
    },
    options
  );

  if (result[0].error?.response?.data?.service === "UserManagement") {
    const { statusCode } = result[0].error.response.data;

    if (statusCode === 401) {
      window.sessionStorage.setItem("redirect", window.location.href);
      auth.signIn();
    }

    if (statusCode === 403) {
      redirectToClientSelectPage();
    }
  }

  return result;
};

const usePortalApi = <TResponse>(
  requestConfig: AxiosRequestConfig,
  options?: Options
): UseAxiosResult<TResponse, ApiError> => {
  const auth = useCodatAuth();

  const token = auth.userData?.access_token;

  const result = api.portalInstance.useAxios<TResponse, ApiError>(
    {
      ...requestConfig,
      url: requestConfig.url,
      headers: {
        ...(requestConfig.headers || {}),
        authorization: `Bearer ${token}`,
      },
    },
    options
  );

  if (result[0].error?.response?.data?.service === "PortalApi") {
    const { statusCode } = result[0].error.response.data;

    if (statusCode === 401) {
      window.sessionStorage.setItem("redirect", window.location.href);
      auth.signIn();
    }

    if (statusCode === 403) {
      redirectToClientSelectPage();
    }
  }

  return result;
};

export const GetUserValueFromToken = (key: string): string | null => {
  const auth = useCodatAuth();

  const token = auth.userData?.access_token;

  if (!token) {
    return null;
  }
  const tokenParts = token.split(".");
  if (tokenParts.length <= 1) {
    return null;
  }

  const buf = Buffer.from(tokenParts[1], "base64");
  const decodedTokenPart = buf.toString("utf8");
  const jwtValues = JSON.parse(decodedTokenPart);
  return jwtValues[key];
};

const tryGetClientIdFromTokenOrRedirect = (token: string | undefined) => {
  const clientIdsInToken = getClientIdsFromToken(token);
  const cookieClientId = cookies.get(config.APP_CLIENT_COOKIE_NAME);
  if (!cookieClientId || cookieClientId === emptyGuid) {
    if (clientIdsInToken.length == 1) {
      cookies.set(config.APP_CLIENT_COOKIE_NAME, clientIdsInToken[0], {
        domain: ".codat.io",
      });
      return clientIdsInToken[0];
    } else {
      redirectToClientSelectPage();
    }
  } else {
    if (clientIdsInToken.includes(cookieClientId)) {
      return cookieClientId;
    } else {
      redirectToClientSelectPage();
    }
  }
};

const redirectToClientSelectPage = () => {
  window.location.assign(
    `/select?${stringify({
      redirectUrl: window.location.href,
    })}`
  );
};

const getClientIdsFromToken = (token: string | undefined): string[] => {
  if (!token) {
    return [];
  }

  const tokenParts = token.split(".");
  if (tokenParts.length <= 1) {
    return [];
  }

  const buf = Buffer.from(tokenParts[1], "base64");
  const decodedTokenPart = buf.toString("utf8");
  const jwtValues = JSON.parse(decodedTokenPart);
  const clientIds = jwtValues["codat_client_id"];
  if (!clientIds) {
    return [];
  }

  if (typeof clientIds === "string") {
    return [clientIds];
  } else {
    return clientIds;
  }
};

export const getAuth0idpFromToken = (token: string | undefined): string => {
  if (!token) {
    return "";
  }

  const tokenParts = token.split(".");
  if (tokenParts.length <= 1) {
    return "";
  }

  const buf = Buffer.from(tokenParts[1], "base64");
  const decodedTokenPart = buf.toString("utf8");
  const jwtValues = JSON.parse(decodedTokenPart);
  const sub = jwtValues["sub"];
  const connectionTypeSub = sub.split("|")[0];

  if (!connectionTypeSub) {
    return "";
  }

  if (connectionTypeSub == "auth0") {
    return "database";
  } else if (
    connectionTypeSub == "google-oauth2" ||
    connectionTypeSub == "waad"
  ) {
    return "social";
  } else {
    return "enterprisesso";
  }
};

export default api;
